import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Search } from 'lucide-react';
import { AdminDeleteButton } from '../components/AdminDeleteButton';

const empty = { name: '', code: '', company_id: '', contact_name: '', contact_email: '', contact_phone: '' };

export default function SuppliersPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const fetch = () => api.get('/suppliers').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { fetch(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.code) { toast.error('Name and code are required'); return; }
    try {
      if (editing) { await api.put(`/suppliers/${editing}`, form); toast.success('Supplier updated'); }
      else { await api.post('/suppliers', form); toast.success('Supplier created'); }
      setOpen(false); setForm(empty); setEditing(null); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error saving'); }
  };

  const toggleStatus = async (s) => {
    const ns = s.status === 'active' ? 'inactive' : 'active';
    await api.patch(`/suppliers/${s.supplier_id}/status`, { status: ns }); toast.success(`Supplier ${ns}`); fetch();
  };

  const remove = async (id) => {
    try { await api.delete(`/suppliers/${id}`); toast.success('Supplier deleted'); fetch(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const edit = (s) => { setForm({ name: s.name, code: s.code, company_id: s.company_id || '', contact_name: s.contact_name || '', contact_email: s.contact_email || '', contact_phone: s.contact_phone || '' }); setEditing(s.supplier_id); setOpen(true); };
  const filtered = data.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.code.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="suppliers-page">
      <div className="page-header">
        <div><h1 className="page-title">Suppliers</h1><p className="text-sm text-slate-500 mt-1">{data.length} total suppliers</p></div>
        <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-supplier-btn"><Plus size={16} className="mr-1.5" />Add Supplier</Button>
      </div>
      <div className="mb-4 relative max-w-sm"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Search suppliers..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-slate-200" data-testid="search-suppliers" /></div>
      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Code</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            filtered.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No suppliers found</TableCell></TableRow> :
            filtered.map(s => (
              <TableRow key={s.supplier_id} className="hover:bg-slate-50/50">
                <TableCell className="font-medium text-slate-900">{s.name}</TableCell>
                <TableCell className="font-mono text-xs text-slate-600">{s.code}</TableCell>
                <TableCell><span className="text-sm text-slate-600">{s.contact_name}</span>{s.contact_email && <span className="block text-xs text-slate-400">{s.contact_email}</span>}</TableCell>
                <TableCell><button onClick={() => toggleStatus(s)} className={`status-badge cursor-pointer status-${s.status}`}>{s.status}</button></TableCell>
                <TableCell><div className="flex items-center gap-1"><Button variant="ghost" size="sm" onClick={() => edit(s)}><Pencil size={14} /></Button><AdminDeleteButton itemName={s.name} description={`Permanently delete supplier "${s.name}". This cannot be undone.`} onConfirm={() => remove(s.supplier_id)} testId={`delete-supplier-${s.supplier_id}`} /></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="grid grid-cols-2 gap-4 mt-2">
            <div className="form-field"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-50" data-testid="supplier-name-input" /></div>
            <div className="form-field"><Label>Code *</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="bg-slate-50" data-testid="supplier-code-input" /></div>
            <div className="form-field"><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field"><Label>Contact Email</Label><Input value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field"><Label>Contact Phone</Label><Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} className="bg-slate-50" /></div>
            <div className="col-span-2 flex justify-end gap-2 pt-2"><Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-supplier-btn">Save</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
